// Mirror (pageFollow) — RTDB plumbing for broadcasting your PDF page and
// watching everyone else's. Framework-agnostic: no Vue, no store, no event bus.
//
// Shape: pageFollow/{pid}/{uid}/{sessionId} = {page, cueId?, followUid?, updatedAt}
//
// Why a top-level tree and not projects/{pid}/...: database.rules.json grants
// .write at projects/$pid whenever (!data.exists() && newData.exists()). The RTDB
// projects tree is empty (that data lives in Firestore), so that clause is true
// for the first write into the subtree — and RTDB grants cascade downward and
// cannot be revoked by a deeper rule. A nested "$uid === auth.uid" would be dead
// code, and anyone could push a fake page into someone else's node.
//
// Why {sessionId} and not just {uid}: a stage manager runs a laptop AND an iPad.
// Two sessions under one key fight (last write wins), and closing EITHER tab
// fires onDisconnect().remove() and deletes the node while they're still online.
// Sessions are separate children; most-recent updatedAt wins per uid.

import firebase from 'firebase/app';
// Required. project/header.vue and sub-header.vue call firebase.database() while
// importing only firebase/app, and work only because core/header.vue happens to
// register the database component into the v8 singleton first. Don't inherit that.
import 'firebase/database';

const THROTTLE_MS = 150;
const MAX_PAGE = 20000;
const MAX_ID_LEN = 64;

function newestSession(sessions) {
    let best = null;
    Object.keys(sessions || {}).forEach((sid) => {
        const s = sessions[sid];
        if (!s) return;
        const at = s.updatedAt || 0;
        if (!best || at > (best.updatedAt || 0)) {
            best = s;
        }
    });
    return best;
}

/**
 * Start broadcasting this client's page and subscribing to everyone else's.
 *
 * getPage/getCueId/getFollowUid are LIVE ACCESSORS, deliberately not values.
 * .info/connected fires true on every reconnect, and we re-publish there; a
 * captured payload would republish the page from session start with a fresh
 * server timestamp, which then wins the most-recent-session tiebreak and yanks
 * every follower back to the leader's opening page after any wifi blip.
 *
 * Returns a handle. The caller MUST call stop().
 */
function startSession({ pid, uid, getPage, getCueId, getFollowUid, onPeers }) {
    const db = firebase.database();
    const sessionRef = db.ref(`pageFollow/${pid}/${uid}`).push();
    const peersRef = db.ref(`pageFollow/${pid}`);
    const connectedRef = db.ref('.info/connected');

    const peers = {};
    let stopped = false;
    let timer = null;
    let lastWriteAt = 0;

    function buildPayload() {
        const page = getPage && getPage();
        if (!Number.isFinite(page) || page < 1 || page > MAX_PAGE) {
            return null;
        }
        const payload = {
            page: Math.round(page),
            updatedAt: firebase.database.ServerValue.TIMESTAMP
        };
        const cueId = getCueId && getCueId();
        if (cueId) {
            payload.cueId = String(cueId).slice(0, MAX_ID_LEN);
        }
        const followUid = getFollowUid && getFollowUid();
        if (followUid) {
            payload.followUid = String(followUid).slice(0, MAX_ID_LEN);
        }
        return payload;
    }

    function write() {
        if (stopped) return;
        const payload = buildPayload();
        // The raw NavBus goToPage path is unclamped and can put currentPage past
        // the end of the doc. A rules rejection is a silent revert plus console
        // noise, not a catchable error, so guard before writing rather than after.
        if (!payload) return;
        lastWriteAt = Date.now();
        sessionRef.set(payload).catch((e) => {
            // console.error is captured by errorBuffer.js for the bug reporter.
            console.error('pageFollow: publish failed', e);
        });
    }

    function cancelThrottle() {
        if (timer) {
            clearTimeout(timer);
            timer = null;
        }
    }

    // Leading + trailing. The trailing edge is mandatory: the final page of a
    // held arrow-key burst must land, or followers stop one page short.
    function publishPage() {
        if (stopped) return;
        const since = Date.now() - lastWriteAt;
        if (since >= THROTTLE_MS) {
            write();
        } else if (!timer) {
            timer = setTimeout(() => {
                timer = null;
                write();
            }, THROTTLE_MS - since);
        }
    }

    function emitPeers() {
        if (stopped || !onPeers) return;
        const out = {};
        Object.keys(peers).forEach((peerUid) => {
            const best = newestSession(peers[peerUid]);
            if (best) {
                out[peerUid] = best;
            }
        });
        onPeers(out);
    }

    // child_* on the {pid} node, NOT value. A value listener on the parent
    // re-sends the entire subtree on every descendant change — N writers x N
    // listeners x O(N) payload, on the same main thread that renders the PDF.
    function onPeerUpsert(snap) {
        peers[snap.key] = snap.val();
        emitPeers();
    }

    function onPeerRemoved(snap) {
        delete peers[snap.key];
        emitPeers();
    }

    function onConnected(snap) {
        if (stopped || snap.val() !== true) return;
        // Register the disconnect hook BEFORE writing, and re-register on every
        // reconnect (the server drops it when the socket dies).
        sessionRef.onDisconnect().remove()
            .then(() => { write(); })
            .catch((e) => {
                console.error('pageFollow: onDisconnect registration failed', e);
            });
    }

    peersRef.on('child_added', onPeerUpsert);
    peersRef.on('child_changed', onPeerUpsert);
    peersRef.on('child_removed', onPeerRemoved);
    connectedRef.on('value', onConnected);

    function stop() {
        if (stopped) return;
        stopped = true;
        // Order matters. A trailing throttle timer that fires after we've
        // cancelled the onDisconnect would re-create this node with nothing left
        // to clean it up — a phantom peer, frozen on a page, in every follower's
        // list, surviving tab close and browser restart. Kill the timer first.
        cancelThrottle();
        peersRef.off('child_added', onPeerUpsert);
        peersRef.off('child_changed', onPeerUpsert);
        peersRef.off('child_removed', onPeerRemoved);
        connectedRef.off('value', onConnected);
        sessionRef.onDisconnect().cancel();
        sessionRef.remove().catch((e) => {
            console.error('pageFollow: session cleanup failed', e);
        });
    }

    return { publishPage, stop };
}

export default startSession;
export { startSession };


// WEBPACK FOOTER //
// ./src/lib/pageFollow.js